import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X } from "lucide-react";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "gaonova-cookie-consent-v1";

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
}

export function getStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

function storeConsent(state: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent("gaonova:consent-updated", { detail: state }));
}

export function openCookieSettings() {
  window.dispatchEvent(new Event("gaonova:open-cookie-settings"));
}

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) setOpen(true);

    const handleOpen = () => {
      const cur = getStoredConsent();
      if (cur) {
        setAnalytics(cur.analytics);
        setMarketing(cur.marketing);
      }
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener("gaonova:open-cookie-settings", handleOpen);
    return () => window.removeEventListener("gaonova:open-cookie-settings", handleOpen);
  }, []);

  const acceptAll = () => {
    storeConsent({ necessary: true, analytics: true, marketing: true, decidedAt: new Date().toISOString() });
    setOpen(false);
  };
  const rejectAll = () => {
    storeConsent({ necessary: true, analytics: false, marketing: false, decidedAt: new Date().toISOString() });
    setOpen(false);
  };
  const savePrefs = () => {
    storeConsent({ necessary: true, analytics, marketing, decidedAt: new Date().toISOString() });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 md:p-6",
        "animate-in slide-in-from-bottom-4 duration-300"
      )}
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-background/95 backdrop-blur-md shadow-2xl">
        <div className="flex items-start gap-3 p-4 sm:p-5">
          <div className="hidden sm:flex shrink-0 w-10 h-10 rounded-full bg-primary/10 items-center justify-center">
            <Cookie className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h2 id="cookie-consent-title" className="font-display text-base sm:text-lg font-semibold text-foreground">
                {t("consent.title")}
              </h2>
              <button
                onClick={rejectAll}
                aria-label={t("common.close")}
                className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              {t("consent.description")}
            </p>

            {showDetails && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <ConsentRow
                  title={t("consent.categories.necessary.title")}
                  description={t("consent.categories.necessary.description")}
                  checked
                  disabled
                />
                <ConsentRow
                  title={t("consent.categories.analytics.title")}
                  description={t("consent.categories.analytics.description")}
                  checked={analytics}
                  onChange={setAnalytics}
                />
                <ConsentRow
                  title={t("consent.categories.marketing.title")}
                  description={t("consent.categories.marketing.description")}
                  checked={marketing}
                  onChange={setMarketing}
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {!showDetails && (
                <Button variant="outline" size="sm" onClick={() => setShowDetails(true)}>
                  {t("consent.customize")}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={rejectAll}>
                {t("consent.rejectAll")}
              </Button>
              {showDetails ? (
                <Button size="sm" onClick={savePrefs}>
                  {t("consent.savePreferences")}
                </Button>
              ) : (
                <Button size="sm" onClick={acceptAll}>
                  {t("consent.acceptAll")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}

function ConsentRow({ title, description, checked, disabled, onChange }: RowProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={title}
      />
    </div>
  );
}
